import * as React from 'react';
import * as StringUtil from '../../util/String';

import { CompactPeoplePicker, IBasePicker, IPersonaProps, Icon, assign } from '@fluentui/react';

import { Provider } from '../../redux/scheduling/types';

export interface ProviderPickerProps {
  onProviderSelected: (selected?: Provider) => void;
  providers: Provider[];
  selected?: Provider;
  suggested: Provider[];
}

export interface ProviderPickerState {
  currentSelectedItems?: IPersonaProps[];
  focused: boolean;
  mostRecentlyUsed: IPersonaProps[];
  peopleList: IPersonaProps[];
  selected?: Provider;
}

export class ProviderPicker extends React.Component<ProviderPickerProps, ProviderPickerState> {
  private mru: IPersonaProps[];
  private people: (IPersonaProps & { key: string | number })[];
  private picker: IBasePicker<IPersonaProps> | undefined;

  /*
   * Lifecycle
   */
  constructor(props: ProviderPickerProps) {
    super(props);

    // This is a bit of a hack since we're extending fabric's PeoplePicker
    // if we need more functionality in the future we should custom build it
    // or take another approach
    this.people = this.props.providers.map((provider) => {
      return {
        key: provider.id,
        text: provider.name,
        secondaryText: provider.surname,
        tertiaryText: provider.marketCode || '',
        optionalText: provider.tags ? provider.tags.join(',') : '',
      };
    });
    this.mru = this.props.suggested.map((provider) => {
      return {
        key: provider.id,
        text: provider.name,
        secondaryText: provider.surname,
        tertiaryText: provider.marketCode || '',
        optionalText: provider.tags ? provider.tags.join(',') : '',
      };
    });

    const peopleList: IPersonaProps[] = [];
    this.people.forEach((persona: IPersonaProps) => {
      const target: IPersonaProps = {};

      assign(target, persona);
      peopleList.push(target);
    });

    this.state = {
      currentSelectedItems: [],
      focused: false,
      mostRecentlyUsed: this.mru,
      peopleList: peopleList,
      selected: this.props.selected,
    };
  }

  public componentDidMount() {
    if (this.picker) {
      this.picker.focusInput();
    }
  }

  public render() {
    if (this.state.focused) {
      return (
        <CompactPeoplePicker
          onResolveSuggestions={this.onFilterChanged}
          onEmptyInputFocus={this.returnMostRecentlyUsed}
          getTextFromItem={this.getTextFromItem}
          onBlur={() => this.setState({ focused: false })}
          pickerSuggestionsProps={{
            mostRecentlyUsedHeaderText: 'In-Market',
            noResultsFoundText: 'No results found',
            onRenderNoResultFound: this.onRenderNoResultFound,
          }}
          onRenderSuggestionsItem={this.onRenderSuggestionsItem}
          onItemSelected={this.onItemSelected}
          itemLimit={2}
          inputProps={{
            placeholder: 'Search',
          }}
          componentRef={(p) => {
            this.picker = p!;
            if (this.picker) {
              this.picker.focusInput();
            }
          }}
        />
      );
    } else {
      const maSurname = this.state.selected && this.state.selected.surname ? this.state.selected.surname[0] : '?';
      return (
        <div className="unfocused" onClick={() => this.setState({ focused: true })}>
          {this.state.selected ? `${this.state.selected.name} ${maSurname}.` : 'Unassigned'}
          <Icon className="search-icon" iconName="Search" />
        </div>
      );
    }
  }

  /*
   * Helpers
   */
  private filterPersonasByText(filterText: string): IPersonaProps[] {
    return this.state.peopleList.filter((item) => StringUtil.startsWith(item.text || '', filterText));
  }

  private getTextFromItem(persona: IPersonaProps): string {
    return persona.text as string;
  }

  private listContainsPersona(persona: IPersonaProps, personas: IPersonaProps[]) {
    if (!personas || !personas.length || personas.length === 0) {
      return false;
    }
    return personas.filter((item) => item.text === persona.text).length > 0;
  }

  private onRenderNoResultFound = () => {
    return (
      <div className="no-result-found">
        <div className="msg">No results found</div>
        <div className="clear" onClick={this.clearSelection}>
          Clear selection
        </div>
      </div>
    );
  };

  private onRenderSuggestionsItem(props: IPersonaProps): JSX.Element {
    return (
      <div key={`${props.id}-${props.tertiaryText}`}>
        {props.text} {props.secondaryText} ({props.tertiaryText})
      </div>
    );
  }

  private removeDuplicates(personas: IPersonaProps[], possibleDupes: IPersonaProps[]) {
    return personas.filter((persona) => !this.listContainsPersona(persona, possibleDupes));
  }

  /*
   * Lambdas
   */
  private clearSelection = () => {
    if (this.props.onProviderSelected) {
      this.props.onProviderSelected(undefined);
    }
    this.setState({
      focused: false,
      selected: undefined,
    });
  };

  private onFilterChanged = (filterText: string, currentPersonas?: IPersonaProps[]): IPersonaProps[] | Promise<IPersonaProps[]> => {
    if (filterText && currentPersonas) {
      let filteredPersonas: IPersonaProps[] = this.filterPersonasByText(filterText);
      filteredPersonas = this.removeDuplicates(filteredPersonas, currentPersonas);
      return filteredPersonas;
    } else {
      return [];
    }
  };

  private onItemSelected = (props?: IPersonaProps): IPersonaProps | null => {
    if (!props) {
      return null;
    }
    const key = 'key';
    const provider = this.props.providers.find((p) => p.id === props[key]);
    if (this.props.onProviderSelected) {
      this.props.onProviderSelected(provider);
    }
    this.setState({
      focused: false,
      selected: provider,
    });
    return props;
  };

  private returnMostRecentlyUsed = (currentPersonas?: IPersonaProps[]): IPersonaProps[] | Promise<IPersonaProps[]> => {
    let { mostRecentlyUsed } = this.state;
    mostRecentlyUsed = this.removeDuplicates(mostRecentlyUsed, currentPersonas || []);
    return mostRecentlyUsed;
  };
}
